import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import "./FileDifferenceChecker.css";

const FileDifferenceChecker = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [file1Content, setFile1Content] = useState("");
  const [file2Content, setFile2Content] = useState("");
  const [differences, setDifferences] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const metaDescription =
    "Free Online File Difference Checker by Dhandha Karle - Compare two text files or code snippets instantly. Find differences, track changes, and compare documents online. 100% Free!";
  const pageTitle = "Free File Difference Checker | Compare Files Online | Dhandha Karle";

  const handleFile1Change = (e) => {
    setFile1Content(e.target.value);
  };

  const handleFile2Change = (e) => {
    setFile2Content(e.target.value);
  };

  const handleFileUpload = (e, fileNumber) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (fileNumber === 1) {
          setFile1Content(event.target.result);
        } else {
          setFile2Content(event.target.result);
        }
      };
      reader.readAsText(file);
    }
  };

  const compareFiles = () => {
    const lines1 = file1Content.split("\n");
    const lines2 = file2Content.split("\n");
    const maxLines = Math.max(lines1.length, lines2.length);
    const diffs = [];

    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || "";
      const line2 = lines2[i] || "";

      if (line1 !== line2) {
        diffs.push({
          lineNumber: i + 1,
          file1: line1,
          file2: line2,
          type: !line1 ? "added" : !line2 ? "removed" : "modified",
        });
      }
    }

    setDifferences(diffs);
    setShowResults(true);
  };

  const clearAll = () => {
    setFile1Content("");
    setFile2Content("");
    setDifferences([]);
    setShowResults(false);
  };

  return (
    <div className="app">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="file difference checker, compare files online, text diff tool, code comparison, file compare, diff checker, free file comparison, text difference finder, document comparison tool, Dhandha Karle"
        />
        <meta name="author" content="Dhandha Karle Team" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <link rel="canonical" href="https://dhandhakarle.com/file-difference-checker" />
      </Helmet>

      <header className="tool-header">
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          File Difference Checker
        </motion.h1>
        <motion.p
          className="subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Compare two files or text instantly - 100% Free Forever
        </motion.p>
      </header>

      <section className="diff-tool-section">
        <motion.div
          className="diff-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="file-inputs">
            <div className="file-input-group">
              <h3>File 1 / Original</h3>
              <label className="upload-btn">
                <input
                  type="file"
                  accept=".txt,.js,.jsx,.css,.html,.json,.md,.py,.java,.cpp"
                  onChange={(e) => handleFileUpload(e, 1)}
                  style={{ display: "none" }}
                />
                Upload File 1
              </label>
              <textarea
                value={file1Content}
                onChange={handleFile1Change}
                placeholder="Paste your first file content here or upload a file..."
                className="diff-textarea"
              />
            </div>

            <div className="file-input-group">
              <h3>File 2 / Modified</h3>
              <label className="upload-btn">
                <input
                  type="file"
                  accept=".txt,.js,.jsx,.css,.html,.json,.md,.py,.java,.cpp"
                  onChange={(e) => handleFileUpload(e, 2)}
                  style={{ display: "none" }}
                />
                Upload File 2
              </label>
              <textarea
                value={file2Content}
                onChange={handleFile2Change}
                placeholder="Paste your second file content here or upload a file..."
                className="diff-textarea"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={compareFiles} className="compare-btn">
              Compare Files
            </button>
            <button onClick={clearAll} className="clear-btn">
              Clear All
            </button>
          </div>

          {showResults && (
            <motion.div
              className="results-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>Comparison Results</h2>
              {differences.length === 0 ? (
                <div className="no-diff">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                  <h3>Files are identical!</h3>
                  <p>No differences found between the two files.</p>
                </div>
              ) : (
                <div className="diff-results">
                  <div className="diff-summary">
                    Found {differences.length} difference{differences.length > 1 ? "s" : ""}
                  </div>
                  {differences.map((diff, idx) => (
                    <div key={idx} className={`diff-item diff-${diff.type}`}>
                      <div className="diff-line-number">Line {diff.lineNumber}</div>
                      <div className="diff-content">
                        <div className="diff-old">
                          <span className="diff-label">Original:</span>
                          <code>{diff.file1 || "(empty line)"}</code>
                        </div>
                        <div className="diff-new">
                          <span className="diff-label">Modified:</span>
                          <code>{diff.file2 || "(empty line)"}</code>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </section>

      <section className="tool-features">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Why Use Our File Difference Checker?
        </motion.h2>
        <div className="features-grid">
          {[
            {
              icon: (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              ),
              title: "Lightning Fast",
              desc: "Compare files instantly with real-time results",
            },
            {
              icon: (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              ),
              title: "100% Secure",
              desc: "All processing happens in your browser - no data stored",
            },
            {
              icon: (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              ),
              title: "No Sign-up Required",
              desc: "Start comparing files immediately, completely free",
            },
            {
              icon: (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
              ),
              title: "Code Friendly",
              desc: "Perfect for comparing code, text, JSON, and more",
            },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              className="feature-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              viewport={{ once: true }}
            >
              {feature.icon}
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default FileDifferenceChecker;
